import React, { useEffect, useState } from 'react';
import { Form as AntdForm, DatePicker, Input, Select, Checkbox, Radio, Space } from 'antd-v5';
import dayjs from 'dayjs';
import ReCAPTCHA from 'react-google-recaptcha';
import EcoImg from 'eco-design/base/eco-img';
import { baseUrl } from 'lowcode/utils/service/baseUrl';
import gtm from 'eco-design-reliance/gtm';
import EcoLink from 'eco-design/base/eco-link';
import { StepProps } from '../createStateMachine';
import { RemoteInstallFormContext } from '../index';
import css from '../index.module.scss';
import { useLang } from '@/i18n';
import { rpcMap } from '@/constants';
import getLocal from '@/app/utils/i18n';
import request from '@/utils/request';
import { getCountriesList } from '@/service';

const SECRET_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_REGISTER_SECRET_KEY;
const installList = [
    { label: 'Smart Home Panel 2', value: 'Smart Home Panel 2' },
    { label: 'Smart Home Panel 3 (32 Circuits)', value: 'Smart Home Panel 3 (32 Circuits)' },
    { label: 'Smart Gateway (200A)', value: 'Smart Gateway (200A)' },
];

const Form: React.FC<StepProps<RemoteInstallFormContext>> = ({ send, context }) => {
    const { locale, query } = getLocal();
    const { t } = useLang('questionnaire');

    /** 人机验证 */
    const hl = rpcMap[locale] || rpcMap['us'];

    /** 表单 */
    const [loading, setLoading] = useState(false);
    const [form] = AntdForm.useForm();
    const [countries, setCountries] = useState([]);

    const handleDone = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true); // 开始 loading
            const { checkbox, ...rest } = values;
            handleSubmit(rest);
        } catch (err) {
            console.log('校验失败:', err);
            setLoading(false);

            gtm.push({
                event: 'uaEvent',
                event_name: 'pre_order_submit',
                form_type: 'questionnaire',
                form_info: 'remote_installation_video_support',
                button_name: t('btn4'),
                status: 'fail',
            });
        }
    };
    const handleSubmit = async (params: any) => {
        const newParams = {
            ...params,
            source: query.utm_source ? query.utm_source : undefined,
            // installationExecutor: context.step4 === 'my' ? t('step4_item2') : t('step4_item1'),
            installationDate: dayjs(params.installationDate).format('YYYY-MM-DD'),
            answers: context.answers?.map((i, idx) => ({ ...i, questionId: idx + 1 })), // 各 step 按钮 send 时传入的第二个参数组成的数组
        };
        const res = await request.post(`${baseUrl(locale)}/remoteInstallForm`, {
            data: newParams,
        });
        setLoading(false); // 结束 loading
        // 邮箱重复
        if (res.code === '5022') {
            form.setFields([
                {
                    name: 'email',
                    errors: [t('form_email_duplicate')],
                },
            ]);

            gtm.push({
                event: 'uaEvent',
                event_name: 'pre_order_submit',
                form_type: 'questionnaire',
                form_info: 'remote_installation_video_support',
                button_name: t('btn4'),
                status: 'fail',
            });
            return;
        }
        if (res.code === '0') {
            send('done');
            gtm.push({
                event: 'uaEvent',
                event_name: 'pre_order_submit',
                form_type: 'questionnaire',
                form_info: 'remote_installation_video_support',
                button_name: t('btn4'),
                status: 'success',
            });
        }
    };

    const initCountry = async () => {
        try {
            const res: any = await getCountriesList({
                page: 1,
                size: 250,
            });
            if (res?.code === '0') {
                setCountries(res?.data?.content?.map((d: any) => ({ label: d.name, value: d.name })) || []);
            } else {
                console.error(res?.message);
            }
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        initCountry();
    }, []);

    return (
        <div className={css.step}>
            <div className="step-head">
                <EcoImg
                    pc="https://websiteoss.ecoflow.com/figma-uploads/1777018501009-v7djk7-compressed-1777018499092.png"
                    h5="https://websiteoss.ecoflow.com/figma-uploads/1777025379501-5otd8y-compressed-1777025377383.jpg"
                />
                <span>{t('step_head')}</span>
            </div>
            <div className="Form-title">
                {t.rich('form_title', {
                    // b: (text: string) => <span style={{ fontWeight: 'bold' }}>{text}</span>,
                    li: (text: string) => <span className="list-item">{text}</span>,
                })}
            </div>
            <AntdForm className="Form-content" form={form} disabled={loading} layout="vertical">
                <AntdForm.Item name="fullName" rules={[{ required: true, message: t('form_fullName_required') }]}>
                    <Input placeholder={t('form_fullName')} maxLength={50} />
                </AntdForm.Item>
                <AntdForm.Item name="phone" rules={[{ required: true, message: t('form_phone_required') }]}>
                    <Input placeholder={t('form_phone')} onInput={(e: any) => (e.target.value = e.target.value.replace(/\D/g, ''))} maxLength={20} />
                </AntdForm.Item>
                <AntdForm.Item
                    name="email"
                    rules={[
                        { required: true, message: t('form_email_required') },
                        { type: 'email', message: t('form_email_invalid') },
                    ]}
                >
                    <Input placeholder={t('form_email')} maxLength={100} />
                </AntdForm.Item>
                <AntdForm.Item name="productToInstall" rules={[{ required: true, message: t('form_productToInstall_required') }]}>
                    <Select showSearch placeholder={t('form_productToInstall')} options={installList} />
                </AntdForm.Item>
                {/* <AntdForm.Item name="batteriesInfo" rules={[{ required: true, message: t('form_batteriesInfo_required') }]}>
                    <Input placeholder={t('form_batteriesInfo')} maxLength={20} />
                </AntdForm.Item> */}
                <AntdForm.Item name="installationDate" rules={[{ required: true, message: t('form_installationDate_required') }]}>
                    <DatePicker
                        placeholder={t('form_installationDate')}
                        suffixIcon={
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="3.33337" y="5" width="13.3333" height="11.6667" rx="0.833333" stroke="#83868A" strokeWidth="1.25" />
                                <path d="M6.66663 9.375H13.3333" stroke="#83868A" strokeWidth="1.25" strokeLinecap="round" />
                                <path d="M6.66663 3.3335V5.8335" stroke="#83868A" strokeWidth="1.25" strokeLinecap="round" />
                                <path d="M13.3334 3.3335V5.8335" stroke="#83868A" strokeWidth="1.25" strokeLinecap="round" />
                            </svg>
                        }
                        disabledDate={(current) => current && current < dayjs().endOf('day')}
                    />
                </AntdForm.Item>
                <AntdForm.Item name="address" rules={[{ required: true, message: t('form_address_required') }]}>
                    <Input placeholder={t('form_address')} maxLength={200} />
                </AntdForm.Item>
                <AntdForm.Item name="purchaseChannel" rules={[{ required: true, message: t('form_purchaseChannel_required') }]}>
                    <Input.TextArea placeholder={t('form_purchaseChannel')} maxLength={100} />
                </AntdForm.Item>
                <AntdForm.Item name="notes" rules={[{ required: false }]}>
                    <Input.TextArea placeholder={t('form_notes')} maxLength={200} />
                </AntdForm.Item>
                <AntdForm.Item name="country" rules={[{ required: true, message: t('form_country_required') }]}>
                    <Select showSearch placeholder={t('form_country')} options={countries} />
                </AntdForm.Item>
                <AntdForm.Item
                    name="bestContactTime"
                    className="span-2"
                    label={t('best_contact_time')}
                    required={false}
                    rules={[{ required: true, message: t('form_bestContactTime_required') }]}
                >
                    {/* <div className="form-item-label">{t('best_contact_time')}</div> */}
                    <Radio.Group className="responsive-radio-group">
                        <Space direction="horizontal">
                            <Radio value="9:00 AM - 12:00 PM">9:00 AM - 12:00 PM</Radio>
                            <Radio value="12:00 PM - 2:00 PM">12:00 PM - 2:00 PM</Radio>
                            <Radio value="2:00 PM - 6:00 PM">2:00 PM - 6:00 PM</Radio>
                            <Radio value="Anytime">Anytime</Radio>
                            <Radio value="Other time">Other time</Radio>
                        </Space>
                    </Radio.Group>
                </AntdForm.Item>

                <AntdForm.Item name="recaptchaResponse" rules={[{ required: true, message: t('form_recaptchaResponse_required') }]}>
                    {/* @ts-ignore */}
                    <ReCAPTCHA
                        hl={hl}
                        sitekey={SECRET_KEY as string}
                        onChange={(token) => form.setFieldsValue({ recaptchaResponse: token })}
                        onExpired={() => form.setFieldsValue({ recaptchaResponse: undefined })}
                        theme="light"
                    />
                </AntdForm.Item>
                <AntdForm.Item
                    name="checkbox"
                    className="span-2 Disclaimer-agree"
                    valuePropName="checked"
                    rules={[
                        {
                            validator: (_, value) => {
                                if (value === true) return Promise.resolve();
                                return Promise.reject(new Error(t('form_agree_required')));
                            },
                        },
                    ]}
                >
                    <Checkbox
                        id="disclaimer-agree"
                        onChange={(e) => {
                            gtm.push({ event: 'uaEvent', event_name: 'agree_checkbox', tag: e.target.checked ? 'Agree' : 'Disagree' });
                        }}
                    >
                        {t.rich('disclaimer_agree', {
                            link: (text: string) => (
                                <EcoLink href="https://us.ecoflow.com/pages/live-installation-terms-conditions" target="_blank">
                                    {text}
                                </EcoLink>
                            ),
                        })}
                    </Checkbox>
                </AntdForm.Item>
            </AntdForm>

            <div className="step-btns">
                {/* <button
                    className={`step-btn step-btn-prev ${loading ? 'step-btn-disabled' : ''}`}
                    onClick={() => {
                        if (loading) return;
                        send('BACK');
                        gtm.push({
                            event: 'uaEvent',
                            event_name: 'form_pagination',
                            button_name: t('btn1'),
                            status: 'none',
                        });
                    }}
                >
                    {t('btn1')}
                </button> */}
                <button className={`step-btn step-btn-next ${loading ? 'step-btn-disabled' : ''}`} onClick={loading ? undefined : handleDone}>
                    {t('btn4')}
                </button>
            </div>
        </div>
    );
};
export default Form;
